import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import CrewFilter from "@/components/CrewFilter";
import PinnedViews from "@/components/PinnedViews";
import { toast } from "sonner";
import { format, parse, startOfWeek, getDay, addDays, startOfMonth, endOfMonth, startOfDay, endOfDay } from "date-fns";
import enUS from "date-fns/locale/en-US";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "@/calendar.css";

import { ChevronLeft, ChevronRight, AlertTriangle, X } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { money, fmtDateTime, StatusBadge } from "@/lib/format";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
    getDay,
    locales,
});
const DnDCalendar = withDragAndDrop(Calendar);

const VIEWS = ["month", "week", "day"];

// Half-day slot definitions: AM 8-12, PM 13-17
const SLOTS = {
    AM: { hour: 8, minute: 0, endHour: 12, endMinute: 0, label: "AM" },
    PM: { hour: 13, minute: 0, endHour: 17, endMinute: 0, label: "PM" },
};

function makeSlotDate(date, slot) {
    const d = new Date(date);
    d.setHours(slot.hour, slot.minute, 0, 0);
    return d;
}
function makeSlotEnd(date, slot) {
    const d = new Date(date);
    d.setHours(slot.endHour, slot.endMinute, 0, 0);
    return d;
}

export default function Schedule() {
    const navigate = useNavigate();
    const [view, setView] = useState("month");
    const [date, setDate] = useState(new Date());
    const [jobs, setJobs] = useState([]);
    const [crew, setCrew] = useState([]);
    // Multi-crew filter — empty Set means "show all". Persists for the session
    // (component-state only; resets on full page reload).
    const [selectedCrewIds, setSelectedCrewIds] = useState(() => new Set());
    const [activePresetId, setActivePresetId] = useState(null);
    const [serverPresets, setServerPresets] = useState(null);
    const [err, setErr] = useState("");

    // Load saved presets from the user's profile
    useEffect(() => {
        let cancelled = false;
        api.get("/me/preferences")
            .then(({ data }) => {
                if (cancelled) return;
                const list = data?.preferences?.pinned_calendar_views;
                if (Array.isArray(list)) setServerPresets(list);
            })
            .catch(() => { /* offline / unauth — fall back to localStorage */ });
        return () => { cancelled = true; };
    }, []);

    // Persist on every change (debounced via PinnedViews' lastPersistedRef)
    const persistPresets = useCallback((list) => {
        api.put("/me/preferences", { pinned_calendar_views: list }).catch(() => {
            /* best-effort — localStorage still has the data */
        });
    }, []);

    // Hydrate from URL params on first paint (?view=week&crews=id1,id2)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const v = params.get("view");
        if (v && ["month", "week", "day"].includes(v)) setView(v);
        const c = params.get("crews");
        if (c) {
            const ids = c.split(",").map((s) => s.trim()).filter(Boolean);
            if (ids.length) setSelectedCrewIds(new Set(ids));
        }
        // eslint-disable-next-line
    }, []);

    const sharePreset = useCallback((preset) => {
        const params = new URLSearchParams();
        params.set("view", preset.view);
        if ((preset.crewIds || []).length > 0) {
            params.set("crews", preset.crewIds.join(","));
        }
        const url = `${window.location.origin}/schedule?${params.toString()}`;
        const copy = async () => {
            try {
                await navigator.clipboard.writeText(url);
                toast.success(`"${preset.name}" link copied`, { duration: 3000 });
            } catch (_) {
                toast(url, { description: "Copy this URL to share", duration: 6000 });
            }
        };
        copy();
    }, []);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null); // job for side panel

    // Range for query window
    const range = useMemo(() => {
        if (view === "month") {
            const s = startOfMonth(date);
            const e = endOfMonth(date);
            return { start: addDays(s, -7), end: addDays(e, 14) };
        }
        if (view === "week") {
            const s = startOfWeek(date, { weekStartsOn: 1 });
            return { start: s, end: addDays(s, 7) };
        }
        return { start: startOfDay(date), end: endOfDay(date) };
    }, [view, date]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const [{ data: cdata }, { data: udata }] = await Promise.all([
                api.get("/jobs/calendar", {
                    params: {
                        start: range.start.toISOString(),
                        end: range.end.toISOString(),
                    },
                }),
                api.get("/admin/users", { params: { role: "crew,contractor,supervisor" } }),
            ]);
            setJobs(cdata.jobs);
            setCrew(udata.users.filter((u) => u.is_active));
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [range.start, range.end]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const events = useMemo(
        () =>
            (jobs || [])
                .filter((j) => {
                    // empty selection = show everything
                    if (selectedCrewIds.size === 0) return true;
                    const crewIds = j.crew || [];
                    // include unassigned jobs only when "all" is selected
                    if (crewIds.length === 0) return false;
                    return crewIds.some((id) => selectedCrewIds.has(id));
                })
                .map((j) => {
                    const start = j.scheduled_start ? new Date(j.scheduled_start) : null;
                    const end = j.scheduled_end ? new Date(j.scheduled_end) : null;
                    return {
                        id: j.id,
                        title: `${j.client?.name || "Job"} · ${j.job_number}`,
                        start,
                        end,
                        job: j,
                        allDay: false,
                        resource: {
                            status: j.status,
                            conflict: (j.conflict_crew_ids || []).length > 0,
                        },
                    };
                })
                .filter((e) => e.start && e.end),
        [jobs, selectedCrewIds],
    );

    const eventPropGetter = (event) => {
        const status = event.resource?.status || "scheduled";
        const conflict = event.resource?.conflict;
        return {
            className: `evt-${status}${conflict ? " evt-conflict" : ""}`,
        };
    };

    const persistMove = async (jobId, newStart, newEnd) => {
        try {
            await api.put(`/jobs/${jobId}`, {
                scheduled_start: newStart.toISOString(),
                scheduled_end: newEnd.toISOString(),
            });
            await fetchData();
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    const handleEventDrop = ({ event, start, end, isAllDay }) => {
        let s = new Date(start);
        let e = new Date(end);
        if (view === "month" || isAllDay) {
            // snap to AM slot on dropped day, keep duration as full half-day
            s = makeSlotDate(start, SLOTS.AM);
            e = makeSlotEnd(start, SLOTS.AM);
        } else {
            // snap each to nearest AM/PM bound
            s = snapSlotStart(s);
            e = snapSlotEnd(e);
            if (e <= s) e = makeSlotEnd(s, isMorning(s) ? SLOTS.AM : SLOTS.PM);
        }
        persistMove(event.id, s, e);
    };

    const handleEventResize = ({ event, start, end }) => {
        const s = snapSlotStart(new Date(start));
        let e = snapSlotEnd(new Date(end));
        if (e <= s) e = makeSlotEnd(s, isMorning(s) ? SLOTS.AM : SLOTS.PM);
        persistMove(event.id, s, e);
    };

    // From an empty grid click on Day/Week, create a quick slot for an existing unscheduled job?
    // Skipped — we navigate to job detail for the full UX.
    const conflictCount = events.filter((e) => e.resource?.conflict).length;

    const moveDate = (delta) => {
        const d = new Date(date);
        if (view === "month") d.setMonth(d.getMonth() + delta);
        else if (view === "week") d.setDate(d.getDate() + delta * 7);
        else d.setDate(d.getDate() + delta);
        setDate(d);
    };

    return (
        <div className="page-shell">
            <header className="fade-up flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="eyebrow">Schedule</p>
                    <h1 className="h1 mt-3" data-testid="schedule-title">
                        Job calendar
                    </h1>
                    <p className="text-muted mt-2 max-w-[640px]">
                        Drag jobs to reschedule, resize to change duration. Half-day slots
                        (AM 8–12 · PM 1–5). Conflicts pop with a ⚠ outline.
                    </p>
                </div>
                {conflictCount > 0 && (
                    <div
                        className="brand-card flex items-center gap-3"
                        style={{
                            background: "rgba(210,5,28,0.06)",
                            borderColor: "var(--brand-red)",
                            padding: "0.75rem 1rem",
                        }}
                        data-testid="schedule-conflict-banner"
                    >
                        <AlertTriangle size={18} style={{ color: "var(--brand-red)" }} />
                        <div>
                            <p
                                className="font-display font-bold text-sm"
                                style={{ color: "var(--brand-red)" }}
                            >
                                {conflictCount} job{conflictCount > 1 ? "s" : ""} with crew conflicts
                            </p>
                            <p className="text-xs text-muted">
                                Outlined blocks have a crew member double-booked.
                            </p>
                        </div>
                    </div>
                )}
            </header>

            {/* Controls */}
            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => moveDate(-1)}
                        aria-label="Previous"
                        data-testid="cal-prev"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setDate(new Date())}
                        data-testid="cal-today"
                    >
                        Today
                    </button>
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => moveDate(1)}
                        aria-label="Next"
                        data-testid="cal-next"
                    >
                        <ChevronRight size={16} />
                    </button>
                    <span
                        className="font-display font-bold ml-3"
                        style={{ fontSize: "1.125rem" }}
                        data-testid="cal-label"
                    >
                        {view === "month"
                            ? format(date, "MMMM yyyy")
                            : view === "week"
                              ? `Week of ${format(startOfWeek(date, { weekStartsOn: 1 }), "MMM d, yyyy")}`
                              : format(date, "EEEE, MMM d, yyyy")}
                    </span>
                </div>

                <div className="flex items-center gap-2">
                    {VIEWS.map((v) => (
                        <button
                            key={v}
                            type="button"
                            className={`btn ${view === v ? "btn-primary" : "btn-ghost"}`}
                            onClick={() => {
                                setView(v);
                                setActivePresetId(null);
                            }}
                            data-testid={`cal-view-${v}`}
                        >
                            <span className="capitalize">{v}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Crew filter */}
            <div className="mt-4 flex flex-col gap-2">
                <CrewFilter
                    crews={crew}
                    selectedIds={selectedCrewIds}
                    onChange={(next) => {
                        setSelectedCrewIds(next);
                        setActivePresetId(null);
                    }}
                />
                <PinnedViews
                    currentView={view}
                    currentCrewIds={selectedCrewIds}
                    crewsById={new Map(crew.map((c) => [c.id, c]))}
                    activePresetId={activePresetId}
                    onApply={(preset) => {
                        setView(preset.view);
                        setSelectedCrewIds(new Set(preset.crewIds || []));
                        setActivePresetId(preset.id);
                    }}
                    onClearActive={() => {
                        setActivePresetId(null);
                        setSelectedCrewIds(new Set());
                    }}
                    onShare={sharePreset}
                    serverPresets={serverPresets}
                    onPersist={persistPresets}
                />
            </div>

            {err && (
                <div
                    className="brand-card mt-4"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="schedule-error"
                >
                    {err}
                </div>
            )}

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
                <div
                    className="brand-card p-0 overflow-hidden fade-up relative"
                    style={{ minHeight: 640 }}
                    data-testid="schedule-calendar"
                >
                    {selectedCrewIds.size > 0 && events.length === 0 && !loading && (
                        <div
                            className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6"
                            style={{
                                background: "rgba(255,255,255,0.94)",
                                backdropFilter: "blur(2px)",
                            }}
                            data-testid="schedule-no-match"
                        >
                            <p className="font-display font-bold text-lg">
                                No crews match your filter
                            </p>
                            <p className="text-muted text-sm mt-2 max-w-xs">
                                Adjust the search above, or
                            </p>
                            <button
                                type="button"
                                className="btn btn-secondary mt-3"
                                onClick={() => setSelectedCrewIds(new Set())}
                                data-testid="schedule-no-match-reset"
                            >
                                Show all crews
                            </button>
                        </div>
                    )}
                    <DnDCalendar
                        localizer={localizer}
                        events={events}
                        date={date}
                        view={view}
                        onNavigate={setDate}
                        onView={(v) => {
                            setView(v);
                            setActivePresetId(null);
                        }}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: 720, padding: "0 8px 8px" }}
                        defaultView="month"
                        views={VIEWS}
                        step={60}
                        timeslots={1}
                        min={new Date(1970, 0, 1, 7, 0)}
                        max={new Date(1970, 0, 1, 19, 0)}
                        onSelectEvent={(e) => setSelected(e.job)}
                        onEventDrop={handleEventDrop}
                        onEventResize={handleEventResize}
                        draggableAccessor={() => true}
                        resizable
                        eventPropGetter={eventPropGetter}
                        popup
                    />
                </div>

                <div className="flex flex-col gap-4">
                    {selected ? (
                        <SidePanel
                            job={selected}
                            onClose={() => setSelected(null)}
                            onOpen={() => navigate(`/jobs/${selected.id}`)}
                        />
                    ) : (
                        <EmptyHints loading={loading} count={events.length} />
                    )}

                    <LegendCard />
                </div>
            </div>
        </div>
    );
}

function EmptyHints({ loading, count }) {
    return (
        <div className="brand-card" data-testid="schedule-hints">
            <p className="eyebrow">Quick start</p>
            <ul className="mt-3 text-sm flex flex-col gap-2">
                <li>· {loading ? "Loading…" : `${count} job${count === 1 ? "" : "s"} on screen`}</li>
                <li>· Click any block to open the side panel.</li>
                <li>· Drag a block to reschedule.</li>
                <li>· Drag the bottom edge to resize.</li>
                <li>· In month view, dropping snaps to AM 8–12.</li>
            </ul>
        </div>
    );
}

function LegendCard() {
    const items = [
        { status: "scheduled", label: "Scheduled" },
        { status: "in_progress", label: "In progress" },
        { status: "completed", label: "Completed" },
        { status: "invoiced", label: "Invoiced" },
        { status: "paid", label: "Paid" },
    ];
    return (
        <div className="brand-card" data-testid="schedule-legend">
            <p className="eyebrow">Status legend</p>
            <ul className="mt-3 flex flex-col gap-2 text-sm">
                {items.map((i) => (
                    <li key={i.status} className="flex items-center gap-3">
                        <span
                            className={`inline-block w-4 h-4 rounded evt-${i.status}`}
                            style={{
                                background:
                                    i.status === "scheduled"
                                        ? "var(--brand-red)"
                                        : i.status === "in_progress"
                                          ? "#ed6c02"
                                          : i.status === "completed"
                                            ? "var(--brand-success)"
                                            : i.status === "invoiced"
                                              ? "var(--brand-blue)"
                                              : "#444",
                            }}
                        />
                        {i.label}
                    </li>
                ))}
                <li className="flex items-center gap-3 pt-2 border-t border-[var(--brand-surface)]">
                    <span
                        className="inline-block w-4 h-4 rounded"
                        style={{
                            background: "var(--brand-red)",
                            boxShadow: "0 0 0 2px #fff, 0 0 0 4px var(--brand-red)",
                        }}
                    />
                    Conflict ⚠
                </li>
            </ul>
        </div>
    );
}

function SidePanel({ job, onClose, onOpen }) {
    return (
        <div className="brand-card fade-up" data-testid={`schedule-panel-${job.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div>
                    <p className="eyebrow">Job</p>
                    <h3 className="h3 mt-2">{job.job_number}</h3>
                </div>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={onClose}
                    aria-label="Close"
                >
                    <X size={14} />
                </button>
            </div>
            <p className="font-display font-bold mt-3">{job.client?.name}</p>
            <p className="text-xs text-muted mt-1">{job.job_address}</p>

            <div className="mt-4 flex flex-col gap-2 text-sm">
                <div>
                    <StatusBadge status={job.status} />
                </div>
                <p>
                    <span className="text-muted">Start:</span>{" "}
                    {fmtDateTime(job.scheduled_start)}
                </p>
                <p>
                    <span className="text-muted">End:</span> {fmtDateTime(job.scheduled_end)}
                </p>
                <p>
                    <span className="text-muted">Estimate:</span>{" "}
                    <span className="font-mono">{money(job.estimate_grand_total)}</span>
                </p>
            </div>

            {(job.crew_resolved || []).length > 0 && (
                <div className="mt-4">
                    <p className="text-xs text-muted">Crew</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {job.crew_resolved.map((c) => (
                            <span
                                key={c.id}
                                className={`badge-pill ${job.conflict_crew_ids?.includes(c.id) ? "badge-error" : "badge-crew"}`}
                            >
                                {c.name}
                                {job.conflict_crew_ids?.includes(c.id) ? " ⚠" : ""}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <button
                type="button"
                className="btn btn-primary w-full mt-5"
                onClick={onOpen}
                data-testid="schedule-open-job"
            >
                Open job
            </button>
        </div>
    );
}

// Helpers
function isMorning(d) {
    return d.getHours() < 12;
}
function snapSlotStart(d) {
    const r = new Date(d);
    if (isMorning(r)) r.setHours(8, 0, 0, 0);
    else r.setHours(13, 0, 0, 0);
    return r;
}
function snapSlotEnd(d) {
    const r = new Date(d);
    if (r.getHours() <= 12) r.setHours(12, 0, 0, 0);
    else r.setHours(17, 0, 0, 0);
    return r;
}
